import React, {useState} from "react";
import {Link, useForm, usePage} from "@inertiajs/react";
import {Alert, Button, Container} from "react-bootstrap";
import axios from "axios";
import {FlexToast, useExternalControls} from "laravel-inertia-backend";
import NoNewsletterBanner from "../../Components/Campaigns/NoNewsletterBanner.jsx";
import WizardStepper from "../../Components/Campaigns/Wizard/WizardStepper.jsx";
import StepGrunddaten from "../../Components/Campaigns/Wizard/StepGrunddaten.jsx";
import StepInhalt from "../../Components/Campaigns/Wizard/StepInhalt.jsx";
import StepEmpfaenger from "../../Components/Campaigns/Wizard/StepEmpfaenger.jsx";
import StepZeitplan from "../../Components/Campaigns/Wizard/StepZeitplan.jsx";
import StepPruefen from "../../Components/Campaigns/Wizard/StepPruefen.jsx";

const STEPS = [
    {number: 1, label: "Grunddaten"},
    {number: 2, label: "Inhalt"},
    {number: 3, label: "Empfänger"},
    {number: 4, label: "Zeitplan"},
    {number: 5, label: "Prüfen & Starten"},
];

const STEP_EXPLANATIONS = {
    1: "Name, Absender und Betreff der Kampagne.",
    2: "Der eigentliche Mail-Text, Signatur und ein optionaler Anhang.",
    3: "Empfänger aus einer Excel-Liste importieren.",
    4: "Zeitraum und Uhrzeiten, in denen versendet werden darf.",
    5: "Alles prüfen und den Versand starten.",
};

/**
 * Assistent zur Anlage/Bearbeitung einer E-Mail-Kampagne: fünf Schritte
 * statt eines einzigen langen Formulars. Technisch bleibt es EIN Formular -
 * "Weiter" speichert jeweils den gesamten aktuellen Stand (wie vorher der
 * "Speichern"-Knopf) und schaltet danach einen Schritt weiter. Wird sowohl
 * für "Neue Kampagne" als auch "Kampagne bearbeiten" verwendet - Unterschied
 * ist nur, ob `campaign` aus den Inertia-Props gesetzt ist.
 */
export default function Edit() {
    const {campaign, mailboxes, signatures, placeholders, maxAttachmentMb, recipientsCount, recipientsPreview, schedulePreview} = usePage().props;
    const {addToast} = useExternalControls();
    const [testMailSending, setTestMailSending] = useState(false);
    const [attachmentTooBig, setAttachmentTooBig] = useState(false);
    const [activeStep, setActiveStep] = useState(1);

    const {data, setData, post, processing, errors} = useForm({
        name: campaign?.name ?? "",
        status: campaign?.status ?? "draft",
        subject: campaign?.subject ?? "",
        body_html: campaign?.body_html ?? "",
        campaign_signature_id: campaign?.campaign_signature_id ?? "",
        sender_mailbox: campaign?.sender_mailbox ?? "",
        sender_name: campaign?.sender_name ?? "",
        reply_to: campaign?.reply_to ?? "",
        attachment: null,
        send_start_date: campaign?.send_start_date ?? "",
        send_end_date: campaign?.send_end_date ?? "",
        send_window_start_time: campaign?.send_window_start_time ?? "",
        send_window_end_time: campaign?.send_window_end_time ?? "",
        // Immer als Zahlen führen: aus älteren Datensätzen können die Tage
        // noch als Text ("1") kommen - dann würde includes(1) fehlschlagen und
        // die Wochentage sähen unmarkiert aus.
        send_weekdays: (campaign?.send_weekdays ?? []).map(Number),
        // Pro-Wochentag-Zeitfenster, Schlüssel = Wochentag (Carbon-dayOfWeek).
        // Ein Tag ohne Eintrag hier nutzt das Standard-Zeitfenster oben.
        send_day_windows: campaign?.send_day_windows ?? {},
        total_to_send: campaign?.total_to_send ?? "",
        is_paused: campaign?.is_paused ?? false,
    });

    const handleChange = (e) => {
        const {id, value} = e.target;
        setData(id, value);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0] ?? null;
        setData("attachment", file);
        if (file && maxAttachmentMb && file.size > maxAttachmentMb * 1024 * 1024) {
            setAttachmentTooBig(true);
        } else {
            setAttachmentTooBig(false);
        }
    };

    const toggleWeekday = (value) => {
        const isActive = data.send_weekdays.includes(value);

        if (isActive) {
            // Tag deaktivieren: aus send_weekdays entfernen UND das Tagesfenster
            // verwerfen - sonst bliebe ein toter Eintrag für einen inaktiven Tag.
            const remainingWindows = {...data.send_day_windows};
            delete remainingWindows[value];
            setData({
                ...data,
                send_weekdays: data.send_weekdays.filter((day) => day !== value),
                send_day_windows: remainingWindows,
            });
        } else {
            // Tag aktivieren: mit dem Standard-Zeitfenster vorbelegen (bzw.
            // 08:00/17:00, falls auch das Standard-Fenster leer ist).
            setData({
                ...data,
                send_weekdays: [...data.send_weekdays, value],
                send_day_windows: {
                    ...data.send_day_windows,
                    [value]: data.send_day_windows[value] ?? {
                        start: data.send_window_start_time || "08:00",
                        end: data.send_window_end_time || "17:00",
                    },
                },
            });
        }
    };

    const updateDayWindow = (weekday, key, value) => {
        setData("send_day_windows", {
            ...data.send_day_windows,
            [weekday]: {
                ...(data.send_day_windows[weekday] ?? {}),
                [key]: value,
            },
        });
    };

    // Persistiert den GESAMTEN aktuellen Formular-Stand (genau wie der
    // frühere einzelne "Speichern"-Knopf) und springt bei Erfolg optional
    // zum nächsten Schritt. Bleibt bei Validierungsfehlern auf dem
    // aktuellen Schritt stehen - die rote Fehlerliste oben zeigt, was fehlt.
    const persist = (nextStep) => {
        const options = {
            forceFormData: true,
            preserveScroll: true,
            onSuccess: () => {
                if (nextStep) {
                    setActiveStep(nextStep);
                }
            },
        };
        if (campaign) {
            post(`/campaigns/${campaign.id}`, options);
        } else {
            post("/campaigns", options);
        }
    };

    const handleTestMail = async () => {
        if (!campaign) {
            return;
        }
        setTestMailSending(true);
        try {
            const response = await axios.post(`/campaigns/${campaign.id}/test-mail`);
            addToast(<FlexToast title="Test-Mail" autohide={true} message={response.data.message}/>);
        } catch (e) {
            const msg = e.response?.data?.message || e.message;
            addToast(<FlexToast variant="danger" title="Test-Mail" autohide={false} message={msg}/>);
        } finally {
            setTestMailSending(false);
        }
    };

    // Neue, noch nicht gespeicherte Kampagne: erst Schritt 1 anspringbar,
    // da Empfänger/Zeitplan/Prüfen eine Kampagnen-ID brauchen. Bei einer
    // bestehenden Kampagne sind alle Schritte frei anspringbar.
    const canJumpTo = (stepNumber) => !!campaign || stepNumber === 1;
    const goToStep = (stepNumber) => {
        if (canJumpTo(stepNumber)) {
            setActiveStep(stepNumber);
        }
    };

    const isLastStep = activeStep === STEPS.length;

    return (
        <Container fluid className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h1 className="mb-0">{campaign ? `Kampagne bearbeiten - ${campaign.name}` : "Neue Kampagne"}</h1>
                <div className="d-flex gap-2">
                    {campaign && (
                        <Link href={`/campaigns/${campaign.id}/recipients`} className="btn btn-outline-secondary">
                            Zur Kampagnen-Ansicht
                        </Link>
                    )}
                    <Link href="/campaigns" className="btn btn-outline-secondary">
                        Zur Übersicht
                    </Link>
                    <Button variant="secondary" disabled={processing} onClick={() => persist(null)}>
                        Speichern
                    </Button>
                </div>
            </div>

            <NoNewsletterBanner/>

            {Object.keys(errors).length > 0 && (
                <Alert variant="danger">
                    <strong>Die Kampagne wurde NICHT gespeichert.</strong> Bitte folgende Felder prüfen:
                    <ul className="mb-0 mt-2">
                        {Object.entries(errors).map(([field, message]) => (
                            <li key={field}>{message}</li>
                        ))}
                    </ul>
                </Alert>
            )}

            <WizardStepper steps={STEPS} activeStep={activeStep} onSelect={goToStep} canJumpTo={canJumpTo}/>

            <p className="text-muted">{STEP_EXPLANATIONS[activeStep]}</p>

            {activeStep === 1 && (
                <StepGrunddaten data={data} errors={errors} handleChange={handleChange} mailboxes={mailboxes}/>
            )}
            {activeStep === 2 && (
                <StepInhalt
                    data={data}
                    setData={setData}
                    errors={errors}
                    signatures={signatures}
                    placeholders={placeholders}
                    maxAttachmentMb={maxAttachmentMb}
                    campaign={campaign}
                    attachmentTooBig={attachmentTooBig}
                    handleFileChange={handleFileChange}
                    testMailSending={testMailSending}
                    handleTestMail={handleTestMail}
                />
            )}
            {activeStep === 3 && (
                <StepEmpfaenger campaign={campaign} recipientsCount={recipientsCount} recipientsPreview={recipientsPreview}/>
            )}
            {activeStep === 4 && (
                <StepZeitplan
                    data={data}
                    errors={errors}
                    handleChange={handleChange}
                    toggleWeekday={toggleWeekday}
                    updateDayWindow={updateDayWindow}
                    recipientsCount={recipientsCount}
                    schedulePreview={schedulePreview}
                />
            )}
            {activeStep === 5 && (
                <StepPruefen
                    data={data}
                    campaign={campaign}
                    recipientsCount={recipientsCount}
                    mailboxes={mailboxes}
                    signatures={signatures}
                    goToStep={goToStep}
                    schedulePreview={schedulePreview}
                />
            )}

            <div className="d-flex justify-content-between mt-3">
                <Button variant="outline-secondary" disabled={activeStep === 1}
                        onClick={() => setActiveStep(Math.max(1, activeStep - 1))}>
                    Zurück
                </Button>
                {!isLastStep && (
                    <Button variant="primary" disabled={processing} onClick={() => persist(activeStep + 1)}>
                        Weiter
                    </Button>
                )}
                {isLastStep && (
                    <Button variant="primary" disabled={processing} onClick={() => persist(null)}>
                        Speichern
                    </Button>
                )}
            </div>
        </Container>
    );
}
